/**
 * PJ の差分（`/pjs/$pjNumber/diff`）の組み立て — 要素の並べ方・絞り込み・件数・欄の値の文字化。
 *
 * **欄の突き合わせ（どの欄が変わったか）はここで決めない。** 決めるのは API の `getPjDiff` で、
 * MCP の `get_spec_changes` と同じ 1 関数が返す `fields[].changed` をそのまま使う。画面で行の
 * `before` / `after` を比べ直すと、画面と MCP で「変わった欄」が別々に決まる。ここが持つのは、
 * 返った要素をどう並べ・どう絞り・どう数えるかだけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の要素を型ごとに並べ、型と操作で絞り、件数と欄の表示値を決める。
 * @graph-connects none
 */
import { isPjDiffHiddenField, type GetPjDiffOutput } from '@cortex/domain-spec-console';

import { SPEC_NODE_TYPE_VALUES, type SpecNodeTypeValue } from '../lib/spec-node-types.js';

/** 差分の要素 1 件。 @graph-connects none */
export type PjDiffViewItem = GetPjDiffOutput['items'][number];

/** 要素の操作（追加 / 変更 / 削除）。 @graph-connects none */
export type PjDiffChangeKind = PjDiffViewItem['changeKind'];

/**
 * 操作の並び。絞り込みの切替と集計はこの順に出す。
 * @graph-connects none
 */
export const PJ_DIFF_CHANGE_KINDS: readonly PjDiffChangeKind[] = ['add', 'update', 'delete'];

/**
 * 型の並び。業務 → 業務ルール → 用語 → 画面 → 部品（モック `pj-diff.html` の順）のあとに、
 * モックに無い型を続ける。ここに無い型は現れた順に最後へ並ぶ（黙って落とさない）。
 * @graph-connects none
 */
export const PJ_DIFF_TYPE_ORDER: readonly string[] = [
  'Business',
  'Rule',
  'Term',
  'Screen',
  'Component',
  'Flow',
  'Stage',
  'Actor',
  'Domain',
  'Verb',
  'Unit',
];

/**
 * 絞り込みの状態。空文字は「すべて」。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の絞り込み（型・操作・変わっていない欄を出すか）
 * @graph-connects none
 */
export interface PjDiffFilter {
  readonly nodeType: string;
  readonly changeKind: PjDiffChangeKind | '';
  readonly showUnchanged: boolean;
}

/**
 * 既定の絞り込み。何も絞らず、変わっていない欄も出す（合意 `pj-diff.html` の既定オン）。
 * @graph-connects none
 */
export const DEFAULT_PJ_DIFF_FILTER: PjDiffFilter = {
  nodeType: '',
  changeKind: '',
  showUnchanged: true,
};

/**
 * 要素に現れる型を、決まった順に並べる。
 *
 * @param items 差分の要素
 * @returns 現れた型（決まった順 → それ以外は現れた順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素に現れる型を、表示の順に並べる。
 * @graph-connects none
 */
export function pjDiffTypesOf(items: readonly PjDiffViewItem[]): readonly string[] {
  const present = [...new Set(items.map((item) => item.nodeType))];
  const known = PJ_DIFF_TYPE_ORDER.filter((type) => present.includes(type));
  const unknown = present.filter((type) => !PJ_DIFF_TYPE_ORDER.includes(type));
  return [...known, ...unknown];
}

/**
 * 値ごとの件数を数える。
 *
 * @param values 数える値
 * @returns 値 → 件数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 値の並びを値ごとの件数へ数える。
 * @graph-connects none
 */
function countOf(values: readonly string[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return counts;
}

/**
 * 型ごとの要素の件数。
 *
 * @param items 差分の要素
 * @returns 型 → 件数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素を型ごとに数える。
 * @graph-connects none
 */
export function pjDiffCountByType(items: readonly PjDiffViewItem[]): ReadonlyMap<string, number> {
  return countOf(items.map((item) => item.nodeType));
}

/**
 * 操作ごとの要素の件数。現れない操作も 0 で持つ。
 *
 * @param items 差分の要素
 * @returns 操作 → 件数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素を追加 / 変更 / 削除ごとに数える。
 * @graph-connects none
 */
export function pjDiffCountByKind(
  items: readonly PjDiffViewItem[],
): Readonly<Record<PjDiffChangeKind, number>> {
  const counts = countOf(items.map((item) => item.changeKind));
  return {
    add: counts.get('add') ?? 0,
    update: counts.get('update') ?? 0,
    delete: counts.get('delete') ?? 0,
  };
}

/**
 * 型と操作で要素を絞る（組み合わせ可）。
 *
 * @param items 差分の要素
 * @param filter 絞り込み
 * @returns 残った要素
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素を型と操作で絞る。
 * @graph-connects none
 */
export function filterPjDiffItems(
  items: readonly PjDiffViewItem[],
  filter: Pick<PjDiffFilter, 'nodeType' | 'changeKind'>,
): readonly PjDiffViewItem[] {
  return items.filter(
    (item) =>
      (filter.nodeType === '' || item.nodeType === filter.nodeType) &&
      (filter.changeKind === '' || item.changeKind === filter.changeKind),
  );
}

/**
 * 型ごとの要素のまとまり。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素の型ごとのまとまり（型と、その型の要素）
 * @graph-connects none
 */
export interface PjDiffGroup {
  readonly nodeType: string;
  readonly items: readonly PjDiffViewItem[];
}

/**
 * 要素を型ごとにまとめる。型の順は `pjDiffTypesOf`、型の中は読取が返した順のまま。
 *
 * @param items 差分の要素
 * @returns 型ごとのまとまり
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素を型ごとのまとまりへ束ねる。
 * @graph-connects pjDiffTypesOf [calls] 型の並び
 */
export function groupPjDiffItems(items: readonly PjDiffViewItem[]): readonly PjDiffGroup[] {
  return pjDiffTypesOf(items).map((nodeType) => ({
    nodeType,
    items: items.filter((item) => item.nodeType === nodeType),
  }));
}

/**
 * 要素の中で出す欄。変わっていない欄を出さないときは変わった欄だけにする。
 *
 * @param item 差分の要素 1 件
 * @param showUnchanged 変わっていない欄も出すか
 * @returns 出す欄
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素 1 件の欄のうち、画面に出すものを選ぶ。
 * @graph-connects isPjDiffHiddenField [calls] 出さない欄（要素の番号・元の規則）の判定
 */
export function visiblePjDiffFields(
  item: PjDiffViewItem,
  showUnchanged: boolean,
): PjDiffViewItem['fields'] {
  // 出さない欄（要素の番号・元の規則）は共通部の判定 1 つを通す（読取の名前の表と同じ）
  const shown = item.fields.filter((field) => !isPjDiffHiddenField(field.field));
  return showUnchanged ? shown : shown.filter((field) => field.changed);
}

/** 表示言語ごとの本文を持つ値（`{ ja, en, vi }`）の言語。 @graph-connects none */
const PJ_DIFF_LOCALES: readonly string[] = ['ja', 'en', 'vi'];

/**
 * 値が表示言語ごとの本文（`{ ja, en, vi }` のうち 1 つ以上で、中身が全部文字）かを判定する。
 *
 * @param value 欄の値
 * @returns 表示言語ごとの本文なら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の欄の値が多言語の本文かを判定し、表示言語の文だけを出せるようにする。
 * @graph-connects none
 */
function isLocalizedText(value: unknown): value is Readonly<Record<string, string>> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length > 0 &&
    entries.every(([key, text]) => PJ_DIFF_LOCALES.includes(key) && typeof text === 'string')
  );
}

/**
 * 欄の値を画面に出す文字へ直す。値が無い（`null`）ときは `null` を返し、描く側が空札にする。
 *
 * 文字はそのまま、真偽は綴り、数は名前の表（`getPjDiff` の `labels`）に在れば要素の名前・無ければ
 * 綴り、表示言語ごとの本文（`{ ja, en, vi }`）は表示言語の文（無ければ日本語、それも無ければ最初の
 * 言語）、配列は「／」で繋ぎ（空なら「—」）、オブジェクトは「欄: 値」を「・」で繋ぐ（入れ子は括弧で
 * 囲む）。**JSON のまま出さない**（W3。`{` や `[` から始まる値は読めない）。語の差分は出さない
 * （合意 `pj-diff.html`）ので、欄の単位で丸ごと見せられる形にする。
 *
 * @param value 欄の値
 * @param locale 表示言語
 * @param labels 番号 → 要素の名前
 * @returns 表示する文字。値が無ければ `null`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の欄の値を、表示言語と要素の名前に合わせて、JSON を使わない文字へ直す。
 * @graph-connects textOf [calls] 値 1 つの文字化
 */
export function formatPjDiffValue(
  value: unknown,
  locale = 'ja',
  labels: ReadonlyMap<number, string> = new Map(),
): string | null {
  if (value === null || value === undefined) return null;
  return textOf(value, locale, labels, false);
}

/**
 * 値 1 つを文字にする（`formatPjDiffValue` の中身）。`nested` はオブジェクトを括弧で囲むか。
 * @graph-connects isLocalizedText [calls] 多言語の本文の判定
 */
function textOf(
  value: unknown,
  locale: string,
  labels: ReadonlyMap<number, string>,
  nested: boolean,
): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return labels.get(value) ?? String(value);
  if (typeof value === 'boolean') return String(value);
  if (isLocalizedText(value)) {
    return value[locale] ?? value['ja'] ?? Object.values(value)[0] ?? '';
  }
  if (Array.isArray(value)) {
    return value.length === 0
      ? '—'
      : value.map((entry: unknown) => textOf(entry, locale, labels, true)).join(' ／ ');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return '—';
    const text = entries
      .map(([name, entry]) => `${name}: ${textOf(entry, locale, labels, true)}`)
      .join(' ・ ');
    return nested ? `（${text}）` : text;
  }
  // JSON から読んだ値には現れない型（bigint・関数など）は綴らない
  return typeof value === 'bigint' ? value.toString() : '—';
}

/**
 * 型の綴りが詳細画面の行き先を引ける型かを判定する。
 *
 * @param nodeType 要素の型
 * @returns 仕様グラフのノード型なら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素の型が、詳細画面の行き先を引けるノード型かを判定する。
 * @graph-connects none
 */
export function isPjDiffNodeType(nodeType: string): nodeType is SpecNodeTypeValue {
  return (SPEC_NODE_TYPE_VALUES as readonly string[]).includes(nodeType);
}

/**
 * 要素の詳細を開くときに重ねる案件。削除の要素だけ master の姿へ送る。
 *
 * 案件が消す要素は、案件を重ねた姿にもう居ない。重ねて開くと「見つかりません」に着く
 * （承認一覧の `targetPjOf` と同じ判断）。
 *
 * @param item 差分の要素 1 件
 * @param pjNumber 案件番号
 * @returns 重ねる案件番号の綴り。master へ送るなら undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素の詳細を開くとき、削除は master、それ以外は案件を重ねた姿へ送る。
 * @graph-connects none
 */
export function pjDiffDetailPj(item: PjDiffViewItem, pjNumber: number): string | undefined {
  return item.changeKind === 'delete' ? undefined : String(pjNumber);
}

/**
 * 要素の枠の DOM id。目次から要素へ移るときの錨である。
 *
 * @param nodeId 要素の id
 * @returns DOM id
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分の要素の枠を目次から指すための DOM id を作る。
 * @graph-connects none
 */
export function pjDiffAnchorId(nodeId: number): string {
  return `pj-diff-node-${String(nodeId)}`;
}
